/**
 * A real 3D couch built from CSS boxes (six shaded faces each), assembled in
 * perspective space and rotated on its vertical axis — so it reads as a solid
 * object turning ("pivot!"), not a flat image flipping.
 */

import { useI18n } from '../i18n'
import './PivotLogo.css'

interface BoxSpec {
  w: number
  h: number
  d: number
  x: number
  y: number
  z: number
}

function Box({ w, h, d, x, y, z }: BoxSpec) {
  const faces = [
    { k: 'front', t: `translate(-50%,-50%) translateZ(${d / 2}px)`, fw: w, fh: h, b: 1 },
    {
      k: 'back',
      t: `translate(-50%,-50%) rotateY(180deg) translateZ(${d / 2}px)`,
      fw: w,
      fh: h,
      b: 0.55,
    },
    {
      k: 'right',
      t: `translate(-50%,-50%) rotateY(90deg) translateZ(${w / 2}px)`,
      fw: d,
      fh: h,
      b: 0.72,
    },
    {
      k: 'left',
      t: `translate(-50%,-50%) rotateY(-90deg) translateZ(${w / 2}px)`,
      fw: d,
      fh: h,
      b: 0.72,
    },
    {
      k: 'top',
      t: `translate(-50%,-50%) rotateX(90deg) translateZ(${h / 2}px)`,
      fw: w,
      fh: d,
      b: 1.3,
    },
    {
      k: 'bottom',
      t: `translate(-50%,-50%) rotateX(-90deg) translateZ(${h / 2}px)`,
      fw: w,
      fh: d,
      b: 0.45,
    },
  ]
  return (
    <div className="pivot-box" style={{ transform: `translate3d(${x}px, ${y}px, ${z}px)` }}>
      {faces.map((f) => (
        <div
          key={f.k}
          className="pivot-face"
          style={{
            width: `${f.fw}px`,
            height: `${f.fh}px`,
            transform: f.t,
            filter: `brightness(${f.b})`,
          }}
        />
      ))}
    </div>
  )
}

export function PivotLogo({ size = 28 }: { size?: number }) {
  const { t } = useI18n()
  const u = size / 40 // design units → px

  // Couch parts, origin at centre, y increases downward, z toward viewer.
  const parts: BoxSpec[] = [
    { w: 44, h: 9, d: 19, x: 0, y: 4, z: 0 }, // base / frame
    { w: 44, h: 17, d: 5, x: 0, y: -6, z: -7 }, // backrest frame
    { w: 6, h: 15, d: 21, x: -21, y: -1, z: 0 }, // left arm
    { w: 6, h: 15, d: 21, x: 21, y: -1, z: 0 }, // right arm
    { w: 17, h: 4, d: 16, x: -9, y: -0.5, z: 1.5 }, // left seat cushion (flat)
    { w: 17, h: 4, d: 16, x: 9, y: -0.5, z: 1.5 }, // right seat cushion (flat)
    { w: 18, h: 12, d: 3, x: -9, y: -7, z: -4 }, // left back cushion
    { w: 18, h: 12, d: 3, x: 9, y: -7, z: -4 }, // right back cushion
  ]

  return (
    <span
      className="pivot-logo"
      role="img"
      aria-label={t('a11y.logo')}
      style={{
        width: `${size * 1.55}px`,
        height: `${size * 1.2}px`,
        perspective: `${size * 10}px`,
      }}
    >
      <span className="pivot-logo-shadow" />
      <span className="pivot-logo-scene">
        {parts.map((p, i) => (
          <Box key={i} w={p.w * u} h={p.h * u} d={p.d * u} x={p.x * u} y={p.y * u} z={p.z * u} />
        ))}
      </span>
    </span>
  )
}
